/**
 * PWA Web Push (FCM) — client wiring.
 *
 * Registers this device for push notifications and records its FCM token in
 * Firestore (`pushTokens/{token}` = { uid, ua, createdAt, lastSeenAt }), which
 * the Cloud Functions `notifyTaskAssigned` + `taskDueDigest` read via the Admin
 * SDK to deliver "task assigned" / "tasks due" alerts.
 *
 * iOS constraints (handled by the calling UI): Web Push only works when the
 * portal is INSTALLED to the Home Screen (standalone) on iOS 16.4+, and the
 * permission prompt must be triggered by a user gesture. Everything here is
 * guarded with `isSupported()` so unsupported browsers degrade quietly.
 *
 * The push service worker is `public/firebase-messaging-sw.js` (self-initializes
 * from Firebase Hosting's reserved /__/firebase/init.js so no config is baked in).
 */
import { getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging';
import { doc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { app, db, auth } from '../firebase';

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;
const SW_URL = '/firebase-messaging-sw.js';

/** True when this browser can do Web Push at all (FCM + SW + Notification API). */
export async function pushSupported() {
  try {
    return (
      typeof window !== 'undefined' &&
      'serviceWorker' in navigator &&
      'Notification' in window &&
      'PushManager' in window &&
      (await isSupported())
    );
  } catch (e) {
    return false;
  }
}

/** Current Notification permission ('default' | 'granted' | 'denied' | 'unsupported'). */
export function pushPermission() {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}

/**
 * Heuristic: on iOS, Web Push requires the PWA be launched from the Home Screen
 * (standalone display mode). Returns true when we're on iOS but NOT standalone,
 * so the UI can tell the user to "Add to Home Screen" first.
 */
export function iosNeedsInstall() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (!isIOS) return false;
  const standalone =
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;
  return !standalone;
}

/**
 * Request permission (must be called from a user gesture) and register this
 * device's FCM token. Throws a coded Error the UI can branch on:
 *   'unsupported' | 'ios-needs-install' | 'not-configured' | 'not-signed-in'
 *   | 'denied' | 'no-token'.
 * @returns {Promise<string>} the FCM token on success.
 */
export async function enablePushNotifications() {
  if (!(await pushSupported())) throw new Error('unsupported');
  if (iosNeedsInstall()) throw new Error('ios-needs-install');
  if (!VAPID_KEY) throw new Error('not-configured');

  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('not-signed-in');

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('denied');

  // Register the dedicated messaging SW explicitly so getToken uses it (the app
  // doesn't register a general SW — serviceWorkerRegistration is unregister()'d).
  const swReg = await navigator.serviceWorker.register(SW_URL);
  const messaging = getMessaging(app);
  const token = await getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: swReg,
  });
  if (!token) throw new Error('no-token');

  await setDoc(
    doc(db, 'pushTokens', token),
    {
      uid,
      ua: navigator.userAgent,
      createdAt: serverTimestamp(),
      lastSeenAt: serverTimestamp(),
    },
    { merge: true }
  );
  return token;
}

/**
 * Silent token refresh — call once per app session from the portal shell.
 *
 * WHY (found 2026-08-14): getToken() only ever ran inside
 * enablePushNotifications(), i.e. on the explicit opt-in gesture — nothing
 * revalidated the token afterwards. When a browser or iOS rotates or
 * invalidates a push subscription (PWA reinstall, OS update, storage cleanup),
 * the pushTokens doc keeps naming the dead token and FCM KEEPS REPORTING
 * SUCCESS — "accepted and queued" is not "delivered", and there is no error
 * anywhere on the sending side. Measured that day: 4 registered tokens, 4
 * FCM successes, 0 notifications delivered on any device, while the morning
 * taskDueDigest had been logging "7 push(es) to 2 user(s)" into that void
 * daily. getToken() against the live SW registration returns the CURRENT
 * token, so upserting it on every open keeps the doc pointing at a live
 * subscription; the zombie docs die later when FCM finally reports them
 * unregistered and sendPushToUid prunes them.
 *
 * Never prompts (exits unless permission is already granted), never throws
 * (a refresh failure must not break the shell), runs once per session.
 */
let refreshedThisSession = false;
export async function refreshPushToken() {
  if (refreshedThisSession) return null;
  refreshedThisSession = true;
  try {
    if (!(await pushSupported())) return null;
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return null;
    if (!VAPID_KEY) return null;
    const uid = auth.currentUser?.uid;
    if (!uid) return null;
    const swReg = await navigator.serviceWorker.register(SW_URL);
    const messaging = getMessaging(app);
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swReg,
    });
    if (!token) return null;
    // merge WITHOUT createdAt: a refresh must not masquerade as a new opt-in,
    // and lastSeenAt is the field that says "this subscription is alive".
    await setDoc(
      doc(db, 'pushTokens', token),
      { uid, ua: navigator.userAgent, lastSeenAt: serverTimestamp() },
      { merge: true }
    );
    return token;
  } catch (e) {
    return null;
  }
}

/**
 * Subscribe to foreground messages (app open + focused). Returns an unsubscribe
 * function. No-op (returns a noop unsubscribe) on unsupported browsers.
 */
export async function listenForForegroundMessages(callback) {
  if (!(await pushSupported())) return () => {};
  const messaging = getMessaging(app);
  return onMessage(messaging, callback);
}

/** Unregister this device's token (e.g. a "disable notifications" action). */
export async function disablePushNotifications() {
  if (!(await pushSupported())) return;
  try {
    const messaging = getMessaging(app);
    const token = await getToken(messaging, { vapidKey: VAPID_KEY }).catch(() => null);
    if (token) await deleteDoc(doc(db, 'pushTokens', token));
  } catch (e) {
    /* best-effort */
  }
}
